import React from "react";
import ReactDOM from "react-dom/client";
import "./index.css";
import App from "./App";
import LottoApp from "./lotto/LottoApp";
import reportWebVitals from "./reportWebVitals";
import { ConfigProvider } from "./contexts/ConfigContext";
import { JWTProvider } from "./contexts/JWTContext";
import { Provider as ReduxProvider } from "react-redux";
import store from "./store/store";
import { setSession, saveStoredUser } from "./utils/authSession";

function hexToRgb(hex) {
  if (!hex) return null;
  let c = hex.trim();
  if (c.startsWith("#")) c = c.slice(1);
  if (c.length === 3) {
    c = c
      .split("")
      .map((ch) => ch + ch)
      .join("");
  }
  const num = parseInt(c, 16);
  if (Number.isNaN(num) || c.length !== 6) return null;
  return {
    r: (num >> 16) & 255,
    g: (num >> 8) & 255,
    b: num & 255,
  };
}

function toRgbaString(color, alpha) {
  const rgb = hexToRgb(color);
  if (!rgb) return null;
  return `rgba(${rgb.r}, ${rgb.g}, ${rgb.b}, ${alpha})`;
}

function decodeUserPayload(raw) {
  if (!raw) return null;
  try {
    const cleaned = raw.replace(/\s/g, "");
    if (typeof window !== "undefined" && window.atob) {
      const binary = window.atob(cleaned);
      if (typeof TextDecoder !== "undefined") {
        const decoder = new TextDecoder();
        const bytes = new Uint8Array(binary.length);
        for (let i = 0; i < binary.length; i += 1) {
          bytes[i] = binary.charCodeAt(i);
        }
        return JSON.parse(decoder.decode(bytes));
      }
      let escaped = "";
      for (let i = 0; i < binary.length; i += 1) {
        const hex = binary.charCodeAt(i).toString(16).padStart(2, "0");
        escaped += `%${hex}`;
      }
      return JSON.parse(decodeURIComponent(escaped));
    }
    return JSON.parse(raw);
  } catch (err) {
    return null;
  }
}

function bootstrapAuthFromQuery() {
  if (typeof window === "undefined") return;
  try {
    const url = new URL(window.location.href);
    const token =
      url.searchParams.get("sessionToken") ||
      url.searchParams.get("token") ||
      url.searchParams.get("accessToken");
    const refresh =
      url.searchParams.get("refreshToken") ||
      url.searchParams.get("refresh_token");
    const userId = url.searchParams.get("userId");
    const rawUser = url.searchParams.get("user");
    if (!token && !refresh) {
      if (url.searchParams.has("wpSource")) {
        url.searchParams.delete("wpSource");
        const cleanedOnlySource = url.searchParams.toString();
        const finalUrlOnlySource =
          url.origin +
          url.pathname +
          (cleanedOnlySource ? "?" + cleanedOnlySource : "") +
          (url.hash || "");
        window.history.replaceState({}, document.title, finalUrlOnlySource);
      }
      return;
    }

    if (token) {
      setSession(token, refresh || undefined);
      try {
        localStorage.removeItem("user");
        localStorage.removeItem("currentUser");
      } catch (err) {
        console.error("Clear stored user failed", err);
      }
    } else if (refresh) {
      try {
        localStorage.setItem("refreshToken", refresh);
      } catch (err) {
        console.error("Persist refresh token failed", err);
      }
    }
    if (userId) {
      try {
        localStorage.setItem("userId", userId);
      } catch (err) {
        console.error("Persist userId failed", err);
      }
    }
    if (rawUser) {
      const userObj = decodeUserPayload(rawUser);
      if (userObj) {
        saveStoredUser(userObj);
        if (!userId && userObj.id) {
          try {
            localStorage.setItem("userId", String(userObj.id));
          } catch (err) {
            console.error("Persist userId failed", err);
          }
        }
      }
    }

    const keysToRemove = [
      "sessionToken",
      "token",
      "accessToken",
      "refreshToken",
      "userId",
      "wpSource",
      "refresh_token",
      "user",
    ];
    let mutated = false;
    keysToRemove.forEach((key) => {
      if (url.searchParams.has(key)) {
        url.searchParams.delete(key);
        mutated = true;
      }
    });
    if (mutated) {
      const cleaned = url.searchParams.toString();
      const finalUrl =
        url.origin +
        url.pathname +
        (cleaned ? "?" + cleaned : "") +
        (url.hash || "");
      window.history.replaceState({}, document.title, finalUrl);
    }
  } catch (err) {
    console.error("Auth bootstrap from query failed", err);
  }
}

bootstrapAuthFromQuery();

// Apply theme colors from backend to CSS variables
function applyThemeVars(theme) {
  if (!theme) return;
  const root = document.documentElement;
  const map = {
    headerBarTop: "--headerBarTop",
    headerBarBot: "--headerBarBot",
    primaryColor: "--primaryColor",
    secondaryColor: "--secondaryColor",
    headerColor: "--headerColor",
    mainText: "--mainText",
    subText: "--subText",
    menuTextColor: "--menuTextColor",
    submenuTextColor: "--submenuTextColor",
    iconColor: "--iconColor",
    menuBorder: "--menuBorder",
    sidebarMenuBG: "--sidebarMenuBG",
    menuBG: "--menuBG",
    mainBG: "--mainBG",
  };
  Object.entries(map).forEach(([key, cssVar]) => {
    if (theme[key]) {
      root.style.setProperty(cssVar, theme[key]);
    }
  });
  if (theme.primaryColor) {
    const a10 = toRgbaString(theme.primaryColor, 0.1);
    const a20 = toRgbaString(theme.primaryColor, 0.2);
    const a30 = toRgbaString(theme.primaryColor, 0.3);
    if (a10) root.style.setProperty("--primaryColorA10", a10);
    if (a20) root.style.setProperty("--primaryColorA20", a20);
    if (a30) root.style.setProperty("--primaryColorA30", a30);
  }
}

function getApiBase() {
  const env = (process.env.REACT_APP_API_BASE || "").trim();
  if (env) return env.replace(/\/$/, "");
  const { protocol, hostname } = window.location;
  // Local dev
  if (/^(localhost|127\.0\.0\.1)$/i.test(hostname))
    return "https://api.huay-huay.vip";
  // Already on api.* domain
  if (/^api\./i.test(hostname)) return `${protocol}//${hostname}`;
  // Derive root domain (best-effort)
  const parts = hostname.split(".");
  const root = parts.length >= 2 ? parts.slice(-2).join(".") : hostname;
  return `${protocol}//api.${root}`;
}

async function loadTheme() {
  const base = getApiBase();
  try {
    const url = `${base}/setting/colorsetting?t=${Date.now()}`;
    // eslint-disable-next-line no-console
    console.info("Fetching theme from:", url);
    const res = await fetch(url, { mode: "cors" });
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    const json = await res.json();
    const theme = Array.isArray(json?.data) ? json.data[0] : json?.data;
    if (theme) applyThemeVars(theme);
  } catch (e) {
    // eslint-disable-next-line no-console
    console.error("Theme load failed:", e);
  }
}

// Fire and forget; rendering proceeds with defaults, then updates when ready
loadTheme();

// Phase 3: หวย = UI ใหม่ (โฟลเดอร์ lotto/). ระบบฝาก-ถอน/โปร/กงล้อ/เช็คอิน เดิม
// ยังใช้ผ่านเส้นทาง /menu ซึ่ง render แอปเดิม (App.js)
function RootSwitch() {
  const path = typeof window !== "undefined" ? window.location.pathname : "/";
  const useLegacy = /^\/(menu|login|legacy)(\/|$)/.test(path);
  return useLegacy ? <App /> : <LottoApp />;
}

const root = ReactDOM.createRoot(document.getElementById("root"));
root.render(
  <React.StrictMode>
    <ReduxProvider store={store}>
      <ConfigProvider>
        <JWTProvider>
          <RootSwitch />
        </JWTProvider>
      </ConfigProvider>
    </ReduxProvider>
  </React.StrictMode>,
);

reportWebVitals();
